import { generateKeyBetween } from "fractional-indexing";
import { v4 } from "uuid";
import { rowTypeFromPeriod } from "@package/grid-utils";
import {
  useActivePageId,
  useQueryConditionalFormattingRules,
  useUpsertConditionalFormattingRules,
  useConditionalFormattingCache,
  sortRules,
  type TConditionalFormattingRule,
} from "../../../data";
import { useConditionalFormattingModal } from "../modals/ConditionalFormatting";
import type { GetContextMenuSection, getMenuItemContext } from "./helpers";
import { getIcon } from "./icons";
import { useGrid } from "../stores";
import type { TRangeCell } from "../../../tableUtils";
import { buildRowId } from "../../../data/formatting/helpers";
import { useUserSubscriptionTier } from "../../../data/user";
import { useUserId } from "../../../context/auth/hooks";

export const useGetMenuItemsConditionalFormatting = (): GetContextMenuSection => {
  const { disabledFeatures } = useUserSubscriptionTier();
  const page_id = useActivePageId();
  const user_id = useUserId();
  const rules = useQueryConditionalFormattingRules(page_id);
  const modal = useConditionalFormattingModal();
  const upsert = useUpsertConditionalFormattingRules(page_id);
  const cache = useConditionalFormattingCache(page_id);
  const [gridApi] = useGrid();

  const createPresetRules = async (ctx: ReturnType<typeof getMenuItemContext>) => {
    const selection = ctx.selectedRange;
    if (!selection || selection.length === 0) return;

    const presetRules = [
      {
        rule: "moreThan" as const,
        limit: "0",
        bg_color: "#2EA043",
        note: "Positive values",
        stop_if_true: false,
        bold_text: false,
        invert_text_color: false,
      },
      {
        rule: "lessThan" as const,
        limit: "0",
        bg_color: "#D62728",
        note: "Negative values",
        stop_if_true: false,
        bold_text: false,
        invert_text_color: false,
      },
    ];

    const next: TConditionalFormattingRule[] = [];
    let priority_idx = sortRules(cache.get() ?? [])[0]?.priority_idx;

    for (const rule of presetRules) {
      for (const cell of selection.flat()) {
        const rowType = rowTypeFromPeriod(cell.period);
        priority_idx = generateKeyBetween(priority_idx, null);
        next.push({
          id: v4(),
          column_id: cell.columnId,
          row_id: buildRowId(cell.period, rowType),
          page_id,
          user_id,
          priority_idx,
          ...rule,
        });
      }
    }

    if (next.length > 0) {
      await upsert.mutateAsync(next);

      const columns = selection.flat().map((x: TRangeCell) => x.columnId);
      const rowNodes = selection
        .flat()
        .map((x: TRangeCell) => gridApi?.getRowNode(x.rowId))
        .filter(Boolean);
      gridApi?.refreshCells({ columns, rowNodes, force: true });
    }
  };

  return (ctx) => {
    const rowId = ctx.rowId;
    if (!ctx.isProduct || ctx.isHeaderMenu || !rowId) return [];
    return [
      {
        name: "Conditional Formatting",
        disabled: disabledFeatures?.conditionalFormatting,
        tooltip: disabledFeatures?.conditionalFormatting
          ? "Please upgrade your subscription to access this feature"
          : undefined,
        icon: getIcon("format"),
        subMenu: [
          {
            name: "Add new rule",
            subMenu: [
              {
                name: "Positives green, negatives red",
                action: () => createPresetRules(ctx),
              },
              {
                name: "Custom",
                action: () => {
                  modal.open({
                    active: "add",
                    range: ctx.selectedRange,
                    rules: rules.data ?? [],
                    width: 730,
                    height: 470,
                  });
                },
              },
            ],
          },
          {
            name: "Manage conditional formatting",
            action: () => {
              console.log({
                active: "manage",
                range: ctx.selectedRange,
                rules: rules.data ?? [],
                width: 730,
                height: 470,
              });
              modal.open({
                active: "manage",
                range: ctx.selectedRange,
                rules: rules.data ?? [],
                width: 730,
                height: 470,
              });
            },
          },
        ],
      },
    ];
  };
};
